/**
 * Simple role-based permission system
 * Defines what each role can access across different pages
 */

const ROLES = {
  ADMIN: "admin",
  OPERATIONS: "operations",
  OPERATIONS_H2S: "operations-h2s",
  SALES: "sales",
  SALES_H2S: "sales-h2s",
  FINANCE: "finance",
  STAFF: "staff",
};

// Map roles to permission groups for compatibility with existing system
const ROLE_TO_GROUPS = {
  [ROLES.ADMIN]: ["system-admin", "department-manager", "team-lead", "team-member", "basic-user"],
  [ROLES.OPERATIONS]: ["team-lead", "team-member", "basic-user"],
  [ROLES.OPERATIONS_H2S]: ["team-lead", "team-member", "basic-user"],
  [ROLES.SALES]: ["team-member", "basic-user"],
  [ROLES.SALES_H2S]: ["team-member", "basic-user"],
  [ROLES.FINANCE]: ["team-lead", "team-member", "basic-user"],
  [ROLES.STAFF]: ["basic-user"],
};

const PAGE_PERMISSIONS = {
  dashboard: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: true, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: true, edit: false, delete: false },
    [ROLES.SALES]: { view: true, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: true, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: false, delete: false },
    [ROLES.STAFF]: { view: true, edit: false, delete: false },
  },
  bookings: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: true, edit: true, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: true, edit: true, delete: false },
    [ROLES.SALES]: { view: true, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: true, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: false, delete: false },
    [ROLES.STAFF]: { view: true, edit: false, delete: false },
  },
  schedules: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: true, edit: true, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: true, edit: true, delete: false },
    [ROLES.SALES]: { view: true, edit: true, delete: false },
    [ROLES.SALES_H2S]: { view: true, edit: true, delete: false },
    [ROLES.FINANCE]: { view: true, edit: false, delete: false },
    [ROLES.STAFF]: { view: true, edit: false, delete: false },
  },
  drivers: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: true, edit: true, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: true, edit: true, delete: false },
    [ROLES.SALES]: { view: true, edit: true, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: false, delete: false },
    [ROLES.STAFF]: { view: true, edit: false, delete: false },
  },
  accounts: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: true, edit: true, delete: false },
    [ROLES.SALES_H2S]: { view: true, edit: true, delete: false },
    [ROLES.FINANCE]: { view: true, edit: false, delete: false },
    [ROLES.STAFF]: { view: true, edit: false, delete: false },
  },
  // organization: {
  //   [ROLES.ADMIN]: { view: true, edit: true, delete: true },
  //   [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
  //   [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
  //   [ROLES.SALES]: { view: false, edit: false, delete: false },
  //   [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
  //   [ROLES.FINANCE]: { view: false, edit: false, delete: false },
  //   [ROLES.STAFF]: { view: false, edit: false, delete: false },
  // },
  calendars: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: true, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: true, edit: false, delete: false },
    [ROLES.FINANCE]: { view: false, edit: false, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  configManagement: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: false, edit: false, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  exportAreaSettings: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: false, edit: false, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  regions: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: true, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: true, edit: false, delete: false },
    [ROLES.SALES]: { view: true, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: true, edit: false, delete: false },
    [ROLES.FINANCE]: { view: false, edit: false, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  "app-versions": {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: true, edit: true, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: true, edit: true, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: false, edit: false, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  dataMigration: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: false, edit: false, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  ghostDocuments: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: false, edit: false, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  finance: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: true, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  "minimum-wages": {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: true, delete: true },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  "driver-bills": {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: true, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  "driver-wallets": {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: true, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  reporting: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: false, edit: false, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: false, edit: false, delete: false },
    [ROLES.SALES]: { view: false, edit: false, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: true, delete: false },
    [ROLES.STAFF]: { view: false, edit: false, delete: false },
  },
  compliance: {
    [ROLES.ADMIN]: { view: true, edit: true, delete: true },
    [ROLES.OPERATIONS]: { view: true, edit: true, delete: false },
    [ROLES.OPERATIONS_H2S]: { view: true, edit: true, delete: false },
    [ROLES.SALES]: { view: true, edit: true, delete: false },
    [ROLES.SALES_H2S]: { view: false, edit: false, delete: false },
    [ROLES.FINANCE]: { view: true, edit: false, delete: false },
    [ROLES.STAFF]: { view: true, edit: false, delete: false },
  },
};

/**
 * Check if a role has permission for a specific action on a page
 * @param {string} role - User's role
 * @param {string} page - Page name (bookings, schedules, drivers, accounts, regions)
 * @param {string} action - Action (view, edit, delete)
 * @returns {boolean} - Whether the user has permission
 */
export const hasPermission = (role, page, action = "view") => {
  if (!role || !page) return false;

  // Handle different role formats (string or object)
  let normalizedRole;
  if (typeof role === "string") {
    normalizedRole = role.toLowerCase();
  } else if (typeof role === "object" && role.value) {
    // Handle object format like { value: 'admin', label: 'Admin' }
    normalizedRole = role.value.toLowerCase();
  } else if (typeof role === "object" && role.name) {
    // Handle object format like { name: 'admin' }
    normalizedRole = role.name.toLowerCase();
  } else {
    // Unknown format, return false
    return false;
  }

  const pagePermissions = PAGE_PERMISSIONS[page];
  if (!pagePermissions) return false;

  const rolePermissions = pagePermissions[normalizedRole];
  if (!rolePermissions) return false;

  return rolePermissions[action] === true;
};

/**
 * Check if user can view a page
 * @param {string} role - User's role
 * @param {string} page - Page name
 * @returns {boolean}
 */
export const canViewPage = (role, page) => {
  return hasPermission(role, page, "view");
};

/**
 * Check if user can edit on a page
 * @param {string} role - User's role
 * @param {string} page - Page name
 * @returns {boolean}
 */
export const canEdit = (role, page) => {
  return hasPermission(role, page, "edit");
};

/**
 * Check if user can delete on a page
 * @param {string} role - User's role
 * @param {string} page - Page name
 * @returns {boolean}
 */
export const canDelete = (role, page) => {
  return hasPermission(role, page, "delete");
};

/**
 * Get all permissions for a role
 * @param {string} role - User's role
 * @returns {object} - Object with page permissions
 */
export const getRolePermissions = (role) => {
  const permissions = {};
  Object.keys(PAGE_PERMISSIONS).forEach((page) => {
    permissions[page] = PAGE_PERMISSIONS[page][role] || {
      view: false,
      edit: false,
      delete: false,
    };
  });
  return permissions;
};

/**
 * Get permission groups that should be assigned to a role
 * @param {string} role - User's role
 * @returns {array} - Array of permission group names
 */
export const getRolePermissionGroups = (role) => {
  return ROLE_TO_GROUPS[role] || [];
};

/**
 * Check if a role should have a specific permission group
 * @param {string} role - User's role
 * @param {string} groupName - Permission group name
 * @returns {boolean}
 */
export const roleHasGroup = (role, groupName) => {
  return ROLE_TO_GROUPS[role]?.includes(groupName) || false;
};

export { ROLES, ROLE_TO_GROUPS };
export default {
  hasPermission,
  canViewPage,
  canEdit,
  canDelete,
  getRolePermissions,
  getRolePermissionGroups,
  roleHasGroup,
  ROLES,
  ROLE_TO_GROUPS,
};
